import { getOrCreateMainLogger } from "../logger/main";
import { windowsIndex } from "./mainStore";
import { Manifest } from "../types/types";
import { objectFlatMap } from "../util";
import {
	OnHeadersReceivedListenerDetails,
	HeadersReceivedResponse,
	BeforeSendResponse,
	OnBeforeSendHeadersListenerDetails,
} from "electron";

const logger = getOrCreateMainLogger();

// Constants for CSP
const HEADER_KEY_CSP = "Content-Security-Policy";
const CSP_VALUE_RESPECT_SERVER_DEFINED_CSP = "respect-server-defined-csp";
const CSP_POLICY_SEPARATOR = ";";

/**
 * The schema allows for an array of policies as well as a string. This function will
 * tramsform an array of policies into a string.
 *
 * @param csp the content security policy definition to turn into a string
 * @returns a string representation of a content security policy
 */
const cspAsString = (csp: string | string[]): string => {
	if (typeof csp !== "string" && !Array.isArray(csp)) {
		throw new TypeError("Invalid CSP string passed; must be a string or array");
	}

	// If "csp" is an array, convert it to a string
	return Array.isArray(csp) ? csp.join(`${CSP_POLICY_SEPARATOR} `).trim() : csp;
};
export class CSPParser {
	directives: any;

	/**
	 * A little CSP header parser. It converts raw CSP to an object
	 * Makes it easy to check, delete directives and reconvert to text.
	 * @example
	 * // Create a new parser
	 * const csp = new CSPParser("default-src 'self'")
	 * // Check for a directive
	 * csp.has('script-src') // false
	 * // Delete a directive
	 * csp.delete('default-src')
	 * // Get object CSP
	 * csp.getObject() // {"default-src": "'self'"}
	 * // Get CSP string
	 * csp.toString() // default-src 'self'
	 *
	 */
	constructor(csp: string | string[] = "") {
		// The actual CSP string to use
		const actualCSP = cspAsString(csp);

		this.directives = {};
		this.convertToObject(actualCSP);
	}

	/**
	 * Converts raw CSP to key/value object.
	 * @param {string} cspString
	 * @returns {void}
	 * @private
	 */
	convertToObject(cspString: string) {
		cspString.split(CSP_POLICY_SEPARATOR).forEach((item) => {
			const tokens = item.trim().split(/\s+/);
			if (tokens[0]) {
				this.directives[tokens[0]] = tokens.slice(1, tokens.length).join(" ");
			}
		});
	}

	/**
	 * Checks whether a directive exists or not.
	 * @param {string} key CSP directive
	 * @returns {boolean}
	 */
	has(key: string) {
		return this.directives.hasOwnProperty(key);
	}

	/**
	 * Deletes a directive. Chainable method
	 * @param {string} key The directive name
	 * @returns {object} this
	 */
	delete(key: string) {
		delete this.directives[key];
		return this;
	}

	/**
	 * Sets a directive. Chainable method
	 * @param {string} key The directive name
	 * @param {string} value Directive value
	 * @returns {object} this
	 */
	set(key: string, value: string) {
		this.directives[key] = value;
		return this;
	}

	/**
	 * Returns a CSP string. Output example:
	 * script-src 'local'; default-src 'somedomain.com';
	 * @returns {string}
	 */
	toString() {
		let out = "";
		for (const directive in this.directives) {
			if (this.directives[directive]) {
				out = `${out}${directive} ${this.directives[directive]}; `;
			}
		}
		return out.trim();
	}

	/**
	 * Returns an object with directives key:value.
	 * @returns {object}
	 */
	getObject() {
		return this.directives;
	}
}

/**
 * Removes certain directives for all CSP responses because they would otherwise prevent
 * preloads from running in the target window.
 *
 * An example list of directives to remove (check the code for an exhaustive list):
 *   script-src
 *   style-src
 *   img-src
 *   default-src
 *   child-src
 */
export const removeFetchDirectives = (
	originalHeaders: Record<string, string[]>,
	url: string
): Record<string, string[]> =>
	objectFlatMap(originalHeaders, (key, headerValues: string[]) => {
		// If any given header key is "content-security-policy" then scrub it
		if (key.toLowerCase() === HEADER_KEY_CSP.toLowerCase()) {
			// A site might send several "content-security-policy" headers with differing restrictions. These
			// will be provided here as headerValues - an array of CSPs (strings). Each CSP might contain
			// the "script-src", "default-src", "style-src" or "img-src" directives. The first two must be
			// removed to enable API injection. All of quote fetch directives must be removed for an injected
			// finsemble titlebar to render correctly.
			const filteredCSPs = headerValues.flatMap((csp) => {
				const parsed = new CSPParser(csp);

				let deleted: string[] = [];
				let deleteDirective = (directive: string) => {
					if (parsed.has(directive)) {
						parsed.delete(directive);
						deleted.push(directive);
					}
				};

				deleteDirective("font-src");
				deleteDirective("script-src");
				deleteDirective("style-src");
				deleteDirective("style-src-attr");
				deleteDirective("style-src-elem");
				deleteDirective("img-src");
				deleteDirective("default-src");
				deleteDirective("child-src");

				if (deleted.length > 0) {
					let msg = `Deleted directives found in CSP for url ${url}`;
					deleted.forEach((directive) => {
						msg += `\t${directive} ${parsed.directives[directive]}`;
					});
					logger.debug(msg);
				}

				//alternative implementation that modifies, rather than removes, CSPs
				// let modifyDirective = (directive: string, modification: string) => {
				// 	if (parsed.has(directive)) {
				// 		let newValue = parsed.directives[directive] + modification;
				// 		logger.debug(`Changing ${directive} directive "${parsed.directives[directive]}" found in CSP to "${newValue}"`, url);
				// 		parsed.set(directive, newValue);
				// 	}
				// };
				// let cleanDirectiveValue = (directive: string, toRemove: RegExp) => {
				// 	if (parsed.has(directive)) {
				// 		let parts: string[] = parsed.directives[directive].split(/\s+/g);
				// 		parts = parts.filter(part => !part.match(toRemove));
				// 		if (parts.length === 0){
				// 			logger.debug(`Deleting ${directive} directive "${parsed.directives[directive]}" found in CSP`, url);
				// 			parsed.delete(directive);
				// 		} else {
				// 			let newValue = parts.join(" ");
				// 			logger.debug(`Changing ${directive} directive "${parsed.directives[directive]}" found in CSP to "${newValue}"`, url);
				// 			parsed.set(directive, newValue);
				// 		}
				// 	}
				// };
				// modifyDirective("script-src", " 'unsafe-eval'");
				// modifyDirective("style-src", " 'unsafe-inline'");
				// modifyDirective("style-src-attr", " 'unsafe-inline'");
				// modifyDirective("style-src-elem", " 'unsafe-inline'");
				// modifyDirective("default-src", " 'unsafe-eval' 'unsafe-inline'");
				// cleanDirectiveValue("default-src", /('nonce-.+')|('strict-dynamic')/g);
				// cleanDirectiveValue("style-src", /('nonce-.+')|('strict-dynamic')/g);
				// deleteDirective("img-src");

				const replacementValue = parsed.toString();
				// If the result is an empty string because we removed all the directives then remove it.
				return replacementValue === "" ? [] : [replacementValue];
			});
			// If there are no remaining CSPs (because perhaps they are all blank after removing directives) then
			// we remove the Content-Security-Policy HTTP header altogether
			const anyRemainingCSPs = filteredCSPs.length > 0;
			return anyRemainingCSPs ? [[key, filteredCSPs]] : [];
		}

		// If the header wasn't content-security-policy then return it unmodified
		return [[key, headerValues]];
	});

/**
 * Substitutes our CSP for any that were received from the server. Servers might
 * set multiple headers with CSP directives, so we remove them all and then add
 * ours to the end of all the headers.
 */
export const substituteOurCSP = (originalHeaders: Record<string, string[]>, csp: string | string[]) => {
	// "preservedCapitalization" starts as HEADER_KEY_CSP ("Content-Security-Policy")
	let preservedCapitalization = HEADER_KEY_CSP;

	// Remove all headers with key "content-security-policy" (case insensitive)
	const substituteHeaders = objectFlatMap(originalHeaders, (key, value) => {
		if (key.toLowerCase() === HEADER_KEY_CSP.toLowerCase()) {
			preservedCapitalization = key;
			return [];
		}
		return [[key, value]];
	});
	// Insert our overridden CSP
	return {
		...substituteHeaders,
		[preservedCapitalization]: [cspAsString(csp)],
	};
};

export const maybeModifyRequestHeaders = (
	details: OnBeforeSendHeadersListenerDetails,
	callback: (headersSendResponse: BeforeSendResponse) => void
) => {
	logger.verbose("main->maybeModifyResponseHeaders");
	const headers = details.requestHeaders ?? {};
	// The id will be 1 for the first page load, representing the main process, but we won't have a record of it
	const webContentsId = details.webContents?.id || details.id;
	// "mainWindow" exists if windowsIndex.byWebContentsId has a key whose value is the id of the requester (details.webContents.id)
	const mainWindow: any =
		!!webContentsId && windowsIndex.byWebContentsId[webContentsId]
			? windowsIndex.byWebContentsId[webContentsId].mainWindow
			: null;

	if (mainWindow) {
		const userAgent = mainWindow.myOpts.userAgentString;
		if (userAgent) {
			headers["User-Agent"] = userAgent;
		}
	}
	callback({ cancel: false, requestHeaders: headers });
};

/**
 * Invoked when headers are received. Modifies responseHeaders to override CSP directives
 * that are received from the server. Servers may send multiple headers with CSP directives.
 * This code will remove all of them in favor of any specified overrides (from either manifest or app config).
 *
 * Further, any "script-src" directives are automatically removed because they will interfere with preloads.
 *
 * More information on how CSP headers work:
 * https://chrisguitarguy.com/2019/07/05/working-with-multiple-content-security-policy-headers/
 */
export const maybeModifyResponseHeaders = (
	manifest: Manifest,
	details: OnHeadersReceivedListenerDetails,
	callback: (headersReceivedResponse: HeadersReceivedResponse) => void
) => {
	logger.verbose("main->maybeModifyResponseHeaders");
	const originalHeaders = details.responseHeaders ?? {};
	// The id will be 1 for the first page load, representing the main process, but we won't have a record of it
	const webContentsId = details.webContents?.id || details.id;
	// "mainWindow" exists if windowsIndex.byWebContentsId has a key whose value is the id of the requester (details.webContents.id)
	const mainWindow: any =
		!!webContentsId && windowsIndex.byWebContentsId[webContentsId]
			? windowsIndex.byWebContentsId[webContentsId].mainWindow
			: {};

	let overrideCSP: string | string[] | null = null;

	// Check the manifest CSP; this will override the server CSP
	const manifestOverrideCSP = manifest[HEADER_KEY_CSP];

	if (
		(Array.isArray(manifestOverrideCSP) && !manifestOverrideCSP.includes(CSP_VALUE_RESPECT_SERVER_DEFINED_CSP)) ||
		(!!manifestOverrideCSP?.trim() && manifestOverrideCSP !== CSP_VALUE_RESPECT_SERVER_DEFINED_CSP)
	) {
		logger.debug(
			`main->maybeModifyResponseHeaders: ${mainWindow.windowName} changing CSP to "${manifestOverrideCSP}" (manifest override)`
		);
		overrideCSP = manifestOverrideCSP;
	}

	if (mainWindow) {
		/**
		 * Content-Security-Policy in appd.json overrides manifest CSP which overrides whatever
		 * is returned from the server.
		 *
		 * This does not work if "Content-Security-Policy" is provided as a parameter to
		 * LauncherClient.spawn();
		 */
		// Check for an app CSP override (this resides in window.options)
		const appOverrideCSP = mainWindow.myOpts?.[HEADER_KEY_CSP];
		// appOverrideCSP may be a string or an array
		if (!!appOverrideCSP) {
			logger.debug(
				`main->maybeModifyResponseHeaders: ${mainWindow.windowName} changing CSP to "${appOverrideCSP}" (app override)`
			);
			overrideCSP = appOverrideCSP;
		}
	}

	// [maybe] replace the original headers received from the server with our own.
	const substitutedHeaders = overrideCSP ? substituteOurCSP(originalHeaders, overrideCSP) : originalHeaders;

	// Remove the fetch directives (default-src, script-src etc.) from any CSP headers
	const replacementHeaders = removeFetchDirectives(substitutedHeaders, details.url);

	callback({
		responseHeaders: replacementHeaders,
	});
};
